import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  AfterViewInit,
  ViewChild,
  HostListener,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { VideoService } from '../video.service';
import { Router } from '@angular/router';
import { Vehicule } from '../../../model/data-managemnt';
import { DataManagementService } from '../../data-management/data-management.service';
import { SigninService } from '../../../signin/signin.service';
import { TranslateService } from '@ngx-translate/core';
import { MediaSaveService } from '../../../utils/media-save/media-save.service';

@Component({
  selector: 'app-video',
  templateUrl: './video.component.html',
  styleUrls: ['./video.component.scss'],
})
export class VideoComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('remote_video', { static: false }) videoElementRef: ElementRef<HTMLVideoElement>;
  vehicule: Vehicule;

  isWakingUp = false;
  wakeupTimeRemaining = 0;
  isRecording = false;
  recordingDurationSeconds = 0;

  private wakeupSubscription: Subscription | null = null;
  private wakeupTimeSubscription: Subscription | null = null;
  private isCleaningUp = false;
  private isDestroyed = false;
  private playbackAutoStarted = false;
  private lastStartedSeekToken: string | null = null;
  private mediaRecorder: any = null;
  private recordedChunks: Blob[] = [];
  private recordingTimer: any = null;
  private recordingStartTime = 0;
  private shouldDownloadRecording = false;
  private recordingStream: MediaStream | null = null;
  private canvasRecordRaf: number | null = null;
  private ownsRecordingStream = false;
  private canvasRecordDraw: (() => void) | null = null;

  constructor(
    public videoService: VideoService,
    public signinService: SigninService,
    public dataManagementService: DataManagementService,
    private router: Router,
    private translate: TranslateService,
    private mediaSaveService: MediaSaveService,
  ) {
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.queryParams) {
      try {
        JSON.parse(navigation.extras.queryParams['type']);
      } catch {
        this.videoService.displayStatus(this.t('VIDEO.error.noVehicleData'), 'error');
      }
    }

    this.vehicule = this.dataManagementService.selectedVehicule;
  }

  private t(key: string): string {
    return this.translate.instant(key);
  }

  get recordingDurationLabel(): string {
    const hours = Math.floor(this.recordingDurationSeconds / 3600);
    const minutes = Math.floor((this.recordingDurationSeconds % 3600) / 60);
    const seconds = this.recordingDurationSeconds % 60;
    return [hours, minutes, seconds].map(v => v.toString().padStart(2, '0')).join(':');
  }

  ngOnInit() {
    this.vehicule = this.dataManagementService.selectedVehicule || this.videoService.vehicule;
    if (!this.vehicule) {
      this.videoService.displayStatus(this.t('VIDEO.error.noVehicle'), 'warning');
      this.router.navigate(['client']);
      return;
    }

    // Live view page defaults to stream mode unless historical playback set it.
    if (this.videoService.type !== 'playback') {
      this.videoService.type = 'stream';
    }

    if (this.videoService.connexion && this.videoService.type === 'stream') {
      this.videoService.teardownVideoSession(this.vehicule);
    }

    this.subscribeToWakeupState();
    this.cancelCountdown();
  }

  ionViewWillEnter() {
    this.isDestroyed = false;
    this.isCleaningUp = false;
    this.vehicule = this.dataManagementService.selectedVehicule || this.videoService.vehicule;
    // Allow a new segment seek to auto-start even if this Ionic page was cached.
    if (this.videoService.type === 'playback') {
      const seek = this.videoService.playbackSeekToken;
      if (seek && seek !== this.lastStartedSeekToken) {
        this.playbackAutoStarted = false;
      }
    } else {
      this.playbackAutoStarted = false;
      this.lastStartedSeekToken = null;
    }
  }

  ionViewDidEnter() {
    this.ensureVideoElementBound();
    this.startPlaybackIfNeeded();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.ensureVideoElementBound();
      this.startPlaybackIfNeeded();
    }, 100);
  }

  ionViewWillLeave() {
    // Keep signaling for return-to-history (`preventMapDestroy` path), but allow
    // a fresh auto-start next time this page is entered with a new seek token.
    this.playbackAutoStarted = false;
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.wakeupSubscription?.unsubscribe();
    this.wakeupTimeSubscription?.unsubscribe();
    this.stopRecordingIfNeeded(false);
    this.performEmergencyCleanup();
  }

  private subscribeToWakeupState(): void {
    this.wakeupSubscription = this.videoService.isWakingUp$.subscribe((waking) => {
      this.isWakingUp = waking;
    });
    this.wakeupTimeSubscription = this.videoService.wakeupTimeRemaining$.subscribe((remaining) => {
      this.wakeupTimeRemaining = remaining;
    });
  }

  private ensureVideoElementBound(): boolean {
    if (this.isDestroyed || !this.videoElementRef?.nativeElement) {
      return false;
    }
    const success = this.videoService.setVideoElement(this.videoElementRef);
    if (success) {
      const video = this.videoElementRef.nativeElement;
      // Keep muted — unmuting without a gesture often yields a black WebView frame.
      video.muted = true;
      video.playsInline = true;
      if ((this.videoService.stream || this.videoService.playback) && video.srcObject) {
        video.play().catch(() => undefined);
      }
      this.initializeVideoStream();
    }
    return success;
  }

  private initializeVideoStream(): void {
    if (this.videoService.type === 'stream' && this.vehicule && !this.videoService.connexion) {
      this.videoService.onConnectClick(this.vehicule);
    }
  }

  private startPlaybackIfNeeded(): void {
    if (this.isDestroyed) {
      return;
    }
    if (this.videoService.type !== 'playback' || !this.vehicule) {
      return;
    }

    const seekToken = this.videoService.playbackSeekToken
      || (this.videoService.path?.displayBeginPathTime
        ? this.videoService.formatPathPlaybackDate(this.videoService.path)
        : '');

    // Already requested for this seek (segment click may have sent it before navigate).
    if (this.videoService.isHistoricalPlaybackPendingOrActive()) {
      this.playbackAutoStarted = true;
      this.lastStartedSeekToken = seekToken || this.lastStartedSeekToken;
      return;
    }

    if (this.playbackAutoStarted && this.lastStartedSeekToken && this.lastStartedSeekToken === seekToken) {
      return;
    }

    this.playbackAutoStarted = true;
    this.lastStartedSeekToken = seekToken || null;
    const channel = this.signinService.hasVehicleFrontCameraOption(this.vehicule) ? 'CH1' : 'CH2';
    void this.videoService.startHistoricalPlayback(channel)
      .catch((err) => {
        console.error('Camera page historical playback failed', err);
        this.playbackAutoStarted = false;
      });
  }

  selectCamera(kind: 'driver' | 'front' | 'rear'): void {
    if (this.isCleaningUp || this.isDestroyed) {
      return;
    }

    if (!this.videoService.isVideoElementReady()) {
      if (!this.ensureVideoElementBound()) {
        this.videoService.displayStatus(this.t('VIDEO.status.initializing'), 'info');
        return;
      }
    }

    if (!this.videoService.connexion) {
      this.videoService.displayStatus(this.t('VIDEO.connection.notConnectedToVehicle'), 'warning');
      this.videoService.onConnectClick(this.vehicule);
      return;
    }

    if (this.isWakingUp) {
      this.videoService.displayStatus(this.t('VIDEO.wakeup.inProgress'), 'info');
      return;
    }

    // User gesture: try to unlock unmuted playback on mobile WebViews.
    const video = this.videoElementRef?.nativeElement;
    if (video) {
      video.muted = false;
      video.play().catch(() => {
        video.muted = true;
        video.play().catch(() => undefined);
      });
    }

    const cameraMap = {
      driver: { stream: 'S5', playback: 'CH2' },
      front: { stream: 'S4', playback: 'CH1' },
      rear: { stream: 'S6', playback: 'CH3' },
    };
    const params = cameraMap[kind];
    this.videoService.streamOrPlayback(params.stream, params.playback);
  }

  captureImage(): void {
    void this.captureImageAsync();
  }

  private async captureImageAsync(): Promise<void> {
    const videoElement = this.videoElementRef?.nativeElement;
    if (!videoElement) {
      this.videoService.displayStatus(this.t('VIDEO.error.videoSetupFailed'), 'error');
      return;
    }

    if (!videoElement.videoWidth || !videoElement.videoHeight || videoElement.readyState < 2) {
      this.videoService.displayStatus(this.t('VIDEO.error.noVideoStream'), 'warning');
      return;
    }

    try {
      await videoElement.play().catch(() => undefined);
      // Wait one painted frame so WebRTC surface is readable on Android WebView.
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
      await new Promise<void>((resolve) => setTimeout(resolve, 80));

      const canvas = document.createElement('canvas');
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) {
        this.videoService.displayStatus(this.t('VIDEO.error.unknownError'), 'error');
        return;
      }
      context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      // Prefer JPEG; fall back to PNG if JPEG encoding fails.
      let blob = await this.canvasToBlob(canvas, 'image/jpeg', 0.92);
      let extension = 'jpg';
      if (!blob || blob.size === 0) {
        blob = await this.canvasToBlob(canvas, 'image/png');
        extension = 'png';
      }
      if (!blob || blob.size === 0) {
        this.videoService.displayStatus(this.t('VIDEO.controls.captureFailed'), 'error');
        return;
      }

      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const vehicleLabel = this.sanitizeFilePart(this.vehicule?.matricule || 'vehicle');
      const fileName = `capture_${vehicleLabel}_${timestamp}.${extension}`;
      await this.mediaSaveService.saveBlob(blob, fileName);
      this.videoService.displayStatus(this.t('VIDEO.controls.imageCaptured'), 'success');
    } catch (error) {
      console.error('Could not capture image:', error);
      this.videoService.displayStatus(this.t('VIDEO.controls.saveFailed'), 'error');
    }
  }

  toggleRecording(): void {
    if (this.isRecording) {
      this.stopRecordingIfNeeded(true);
      return;
    }
    this.startRecording();
  }

  private startRecording(): void {
    const videoElement = this.videoElementRef?.nativeElement;
    if (!videoElement) {
      this.videoService.displayStatus(this.t('VIDEO.error.videoSetupFailed'), 'error');
      return;
    }

    if (!videoElement.videoWidth || videoElement.readyState < 2) {
      this.videoService.displayStatus(this.t('VIDEO.error.noVideoStream'), 'warning');
      return;
    }

    const RecorderCtor = (window as any).MediaRecorder;
    if (!RecorderCtor) {
      this.videoService.displayStatus(this.t('VIDEO.controls.recordingNotSupported'), 'error');
      return;
    }

    const strategies: Array<() => { stream: MediaStream; owned: boolean } | null> = [];
    const isIos = Capacitor.getPlatform() === 'ios';
    // Build ordered attempts — if MediaRecorder rejects one stream, try the next.
    if (isIos) {
      strategies.push(
        () => this.tryVideoCaptureStream(videoElement),
        () => this.tryCanvasStream(videoElement),
        () => this.trySrcObjectStream(videoElement),
      );
    } else {
      strategies.push(
        () => this.trySrcObjectStream(videoElement),
        () => this.tryVideoCaptureStream(videoElement),
        () => this.tryCanvasStream(videoElement),
      );
    }

    let lastError: unknown = null;
    for (const strategy of strategies) {
      this.stopCanvasRecordingLoop();
      this.releaseOwnedRecordingStream();
      const prepared = strategy();
      if (!prepared) {
        continue;
      }
      try {
        this.beginMediaRecorder(prepared.stream, prepared.owned, RecorderCtor);
        return;
      } catch (error) {
        lastError = error;
        this.stopCanvasRecordingLoop();
        this.releaseOwnedRecordingStream();
      }
    }

    console.error('Could not start recording:', lastError);
    this.videoService.displayStatus(this.t('VIDEO.controls.recordingNotSupported'), 'error');
  }

  private beginMediaRecorder(stream: MediaStream, owned: boolean, RecorderCtor: any): void {
    this.recordingStream = stream;
    this.ownsRecordingStream = owned;
    const mimeType = this.getSupportedRecordingMimeType();
    this.recordedChunks = [];
    this.mediaRecorder = mimeType
      ? new RecorderCtor(stream, { mimeType })
      : new RecorderCtor(stream);

    this.mediaRecorder.ondataavailable = (event: any) => {
      if (event.data && event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      this.stopCanvasRecordingLoop();
      this.releaseOwnedRecordingStream();
      if (this.shouldDownloadRecording) {
        setTimeout(() => void this.downloadRecordedVideo(), 250);
      } else {
        this.recordedChunks = [];
      }
      this.shouldDownloadRecording = false;
    };

    this.mediaRecorder.onerror = () => {
      this.videoService.displayStatus(this.t('VIDEO.error.unknownError'), 'error');
    };

    this.mediaRecorder.start(250);
    this.isRecording = true;
    this.recordingDurationSeconds = 0;
    this.recordingStartTime = Date.now();
    this.startCanvasRecordingLoop();
    this.startRecordingTimer();
    this.videoService.displayStatus(this.t('VIDEO.controls.recordingStarted'), 'success');
  }

  private tryVideoCaptureStream(
    videoElement: HTMLVideoElement
  ): { stream: MediaStream; owned: boolean } | null {
    const captureFn =
      (videoElement as any).captureStream ||
      (videoElement as any).mozCaptureStream ||
      (videoElement as any).webkitCaptureStream;
    if (typeof captureFn !== 'function') {
      return null;
    }
    try {
      const captured = captureFn.call(videoElement, 15) as MediaStream;
      if (captured?.getTracks?.().length) {
        return { stream: captured, owned: true };
      }
    } catch {
      // ignore
    }
    return null;
  }

  private tryCanvasStream(
    videoElement: HTMLVideoElement
  ): { stream: MediaStream; owned: boolean } | null {
    this.canvasRecordDraw = null;
    if (
      videoElement.videoWidth <= 0 ||
      videoElement.videoHeight <= 0 ||
      typeof (HTMLCanvasElement.prototype as any).captureStream !== 'function'
    ) {
      return null;
    }
    const canvas = document.createElement('canvas');
    canvas.width = videoElement.videoWidth;
    canvas.height = videoElement.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }
    const stream = (canvas as any).captureStream(15) as MediaStream;
    this.canvasRecordDraw = () => {
      if (!this.isRecording) {
        return;
      }
      try {
        ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      } catch {
        // ignore transient draw errors
      }
      this.canvasRecordRaf = requestAnimationFrame(() => this.canvasRecordDraw?.());
    };
    return { stream, owned: true };
  }

  private trySrcObjectStream(
    videoElement: HTMLVideoElement
  ): { stream: MediaStream; owned: boolean } | null {
    const srcObject = videoElement.srcObject as MediaStream | null;
    if (srcObject?.getVideoTracks?.().some((t) => t.readyState === 'live')) {
      return { stream: srcObject, owned: false };
    }
    return srcObject?.getTracks?.().length ? { stream: srcObject, owned: false } : null;
  }

  private startCanvasRecordingLoop(): void {
    if (!this.canvasRecordDraw) {
      return;
    }
    if (this.canvasRecordRaf != null) {
      cancelAnimationFrame(this.canvasRecordRaf);
      this.canvasRecordRaf = null;
    }
    this.canvasRecordDraw();
  }

  private stopCanvasRecordingLoop(): void {
    if (this.canvasRecordRaf != null) {
      cancelAnimationFrame(this.canvasRecordRaf);
      this.canvasRecordRaf = null;
    }
    this.canvasRecordDraw = null;
  }

  private stopRecordingIfNeeded(download: boolean): void {
    if (!this.mediaRecorder) {
      this.isRecording = false;
      this.stopRecordingTimer();
      this.stopCanvasRecordingLoop();
      this.releaseOwnedRecordingStream();
      return;
    }

    const recorder = this.mediaRecorder;
    this.mediaRecorder = null;
    this.isRecording = false;
    this.stopRecordingTimer();
    this.shouldDownloadRecording = download;

    if (!download) {
      recorder.onstop = null;
      this.recordedChunks = [];
      this.shouldDownloadRecording = false;
      this.stopCanvasRecordingLoop();
      this.releaseOwnedRecordingStream();
    }

    if (recorder.state !== 'inactive') {
      if (download) {
        try {
          recorder.requestData();
        } catch {
          // Ignore when requestData is not available.
        }
      }
      recorder.stop();
    } else if (download) {
      void this.downloadRecordedVideo();
      this.shouldDownloadRecording = false;
      this.stopCanvasRecordingLoop();
      this.releaseOwnedRecordingStream();
    }
  }

  private async downloadRecordedVideo(): Promise<void> {
    if (!this.recordedChunks.length) {
      this.videoService.displayStatus(this.t('VIDEO.controls.recordingEmpty'), 'warning');
      return;
    }

    const mime = this.recordedChunks[0].type || 'video/webm';
    const videoBlob = new Blob(this.recordedChunks, { type: mime });
    this.recordedChunks = [];
    if (!videoBlob.size) {
      this.videoService.displayStatus(this.t('VIDEO.controls.recordingEmpty'), 'warning');
      return;
    }

    const extension = mime.includes('mp4') ? 'mp4' : 'webm';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const vehicleLabel = this.sanitizeFilePart(this.vehicule?.matricule || 'vehicle');
    const fileName = `record_${vehicleLabel}_${timestamp}.${extension}`;

    try {
      await this.mediaSaveService.saveBlob(videoBlob, fileName);
      this.videoService.displayStatus(this.t('VIDEO.controls.recordingSaved'), 'success');
    } catch (error) {
      console.error('Could not save recording:', error);
      this.videoService.displayStatus(this.t('VIDEO.controls.saveFailed'), 'error');
    }
  }

  private canvasToBlob(
    canvas: HTMLCanvasElement,
    type: string,
    quality?: number
  ): Promise<Blob | null> {
    return new Promise((resolve) => {
      if (canvas.toBlob) {
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
              return;
            }
            try {
              const dataUrl = canvas.toDataURL(type, quality);
              resolve(this.dataUrlToBlob(dataUrl));
            } catch {
              resolve(null);
            }
          },
          type,
          quality
        );
        return;
      }
      try {
        resolve(this.dataUrlToBlob(canvas.toDataURL(type, quality)));
      } catch {
        resolve(null);
      }
    });
  }

  private dataUrlToBlob(dataUrl: string): Blob | null {
    const parts = dataUrl.split(',');
    if (parts.length < 2) {
      return null;
    }
    const mime = parts[0].match(/:(.*?);/)?.[1] || 'image/png';
    const binary = atob(parts[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
  }

  private sanitizeFilePart(value: string): string {
    return value.replace(/[^\w.-]+/g, '_');
  }

  private releaseOwnedRecordingStream(): void {
    if (this.ownsRecordingStream && this.recordingStream) {
      this.recordingStream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // ignore
        }
      });
    }
    this.ownsRecordingStream = false;
    this.recordingStream = null;
  }

  private getSupportedRecordingMimeType(): string {
    const isIos = Capacitor.getPlatform() === 'ios';
    // Prefer MP4 on Android — gallery often cannot play WebM ("Impossible de lire la vidéo").
    const candidates = isIos
      ? ['video/mp4', 'video/webm;codecs=vp8,opus', 'video/webm']
      : [
          'video/mp4',
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=vp8,opus',
          'video/webm',
        ];
    for (const mimeType of candidates) {
      if ((window as any).MediaRecorder?.isTypeSupported?.(mimeType)) {
        return mimeType;
      }
    }
    return '';
  }

  private startRecordingTimer(): void {
    this.stopRecordingTimer();
    this.recordingTimer = setInterval(() => {
      this.recordingDurationSeconds = Math.floor((Date.now() - this.recordingStartTime) / 1000);
    }, 1000);
  }

  private stopRecordingTimer(): void {
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer);
      this.recordingTimer = null;
    }
    this.recordingDurationSeconds = 0;
  }

  handleHangup() {
    try {
      this.stopRecordingIfNeeded(true);
      this.videoService.hangUpCall(this.vehicule);
    } catch (error) {
      console.error('Error during hangup:', error);
    }
  }

  cancelCountdown() {
    this.videoService.counterLoading = false;
    this.videoService.counter = 0;
  }

  cleanup() {
    this.stopRecordingIfNeeded(true);
    this.videoService.teardownVideoSession(this.vehicule);
    this.videoService.resetService();
  }

  close() {
    this.performEmergencyCleanup();
    this.router.navigate([this.videoService.returnRoute || 'client']);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.performEmergencyCleanup();
    return true;
  }

  @HostListener('window:visibilitychange')
  visibilityChangeHandler() {
    if (document.visibilityState === 'hidden' && this.videoService.type !== 'playback') {
      this.performEmergencyCleanup();
    }
  }

  @HostListener('window:pagehide')
  pageHideHandler() {
    if (this.videoService.type !== 'playback') {
      this.performEmergencyCleanup();
    }
  }

  @HostListener('window:freeze')
  pageFreezeHandler() {
    if (this.videoService.type !== 'playback') {
      this.performEmergencyCleanup();
    }
  }

  private performEmergencyCleanup() {
    if (this.isCleaningUp) {
      return;
    }
    this.isCleaningUp = true;
    try {
      this.stopRecordingIfNeeded(false);
      this.videoService.teardownVideoSession(this.vehicule);
    } catch (e) {
      console.error('Error during emergency cleanup', e);
    }
  }
}
